import React from 'react';
import { getContrastRatio } from '../../utils/colorAnalysis';
import type { HSV } from './types';
import { hsvToRgb, rgbToHex } from '../../utils/colors';

interface ColorSwatchRowProps {
  colors: HSV[];
  activeIndex: number;
  onSelect: (index: number) => void;
}

const ColorSwatchRow: React.FC<ColorSwatchRowProps> = ({ colors, activeIndex, onSelect }) => {
  return (
    <div className="grid grid-cols-3 sm:flex gap-2 w-full">
      {colors.map((hsv, i) => {
        const rgb = hsvToRgb(hsv.h, hsv.s, hsv.v);
        const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
        const isLight = getContrastRatio(hex, '#000000') >= getContrastRatio(hex, '#FFFFFF');
        const isActive = i === activeIndex;
        const isBase = i === 0;

        return (
          <button
            key={i}
            onClick={() => onSelect(i)}
            aria-pressed={isActive}
            className={`
              relative min-w-0 min-h-24 flex-1 rounded-control transition-colors duration-150
              ${isActive
                ? 'ring-2 ring-brand-600 ring-offset-2 shadow-card z-10'
                : 'hover:ring-2 hover:ring-brand-300 hover:ring-offset-2'
              }
            `}
            style={{ backgroundColor: hex }}
            title={`${hex}${isBase ? ' (Base)' : ''}`}
          >
            <div className="pt-[80%]" />

            {/* Hex label */}
            <span
              className={`
                absolute bottom-1.5 left-1/2 -translate-x-1/2
                text-xs font-mono font-medium tracking-tight
                px-1.5 py-0.5 rounded-md backdrop-blur-sm
                ${isLight
                  ? 'text-slate-800 bg-white/50'
                  : 'text-white bg-black/30'
                }
              `}
            >
              {hex.toUpperCase()}
            </span>

            {/* Base badge */}
            {isBase && (
              <span
                className={`
                  absolute top-1.5 left-1/2 -translate-x-1/2
                  text-xs font-bold uppercase tracking-widest
                  px-1.5 py-0.5 rounded-md
                  ${isLight
                    ? 'text-slate-700 bg-white/60'
                    : 'text-white bg-black/30'
                  }
                `}
              >
                Base
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ColorSwatchRow;
